import React from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { AppFieldProps } from '../types/components.type';
import { HttpResponse } from '../services/base.service';
import { AppBox } from './AppBox';

export interface OptionType<T = number | string> {
    id: T;
    name: string;
}

export type AppAutocompleteFieldProps = {
    /* The name of the property where it will be save the option id */
    valueProperty?: string;
    service: {
        /**
         * If you desire static options
         */
        options?: OptionType[];
        /**
         * If you want to load, for instance, from a endpoint
         * @returns 
         */
        load?: () => Promise<HttpResponse>
    };
    multiple?: boolean;
} & AppFieldProps

export function AppAutocompleteField(props: AppAutocompleteFieldProps) {
    const { name, control, label, rules, service, valueProperty = 'id', multiple = true, disabled } = props;

    const [options, setOptions] = React.useState<OptionType[]>(service.options || []);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);


    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                // use static options immediately
                if (service.options) {
                    setOptions(service.options);
                }

                if (service.load) {
                    const response = await service.load();
                    setOptions(response.data as OptionType[]);
                }
            } catch (error) {
                console.error("Error fetching autocomplete options", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [service.options]);

    return (
        <AppBox>
            <Controller
                name={name}
                control={control}
                rules={rules}
                render={({ field, fieldState }) => {

                    let selectedOptions: OptionType | OptionType[] | null = multiple ? [] : null;
                    if (field.value !== undefined && field.value !== null) {
                        selectedOptions = Array.isArray(field.value)
                            ? options.filter(option => field.value.some((val: any) => val[valueProperty] === option.id))
                            : options.find(option => option.id === field.value) || null;
                    }


                    return (
                        <Autocomplete
                            {...field}
                            value={selectedOptions}
                            multiple={multiple}
                            options={options}
                            disabled={disabled}
                            getOptionLabel={(option: OptionType) => option.name}
                            isOptionEqualToValue={(option: OptionType, value: OptionType) => option.id === value.id}
                            onChange={(_, data) => {
                                // Transform data to only include IDs in a object( if multiple) or just a primitive 
                                const transformedData = Array.isArray(data) ?
                                    data.map((item: OptionType) => ({ [valueProperty]: item.id })) : (data?.id ?? '');

                                field.onChange(transformedData);
                                props.onChange?.(transformedData);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={label}

                                    variant="outlined"
                                    fullWidth
                                    error={!!fieldState.error}
                                    helperText={fieldState.error ? fieldState.error.message : ''}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />)
                }}
            />
        </AppBox>
    );
}
